import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { CSSProperties, useEffect, useState, memo } from 'react';

import { ClassNameComponent } from '@/utils/ClassNameComponent';
import ConditionalRenderer from './ConditionalRenderer';
import {
  MILLISECONDS_PER_DAY,
  MILLISECONDS_PER_HOUR,
  MILLISECONDS_PER_MINUTE,
  MILLISECONDS_PER_SECOND,
} from '@/constants';

type CountdownClassNameComponent = ClassNameComponent & {
  item?: string;
  value?: string;
  label?: string;
};

type CountdownProps = {
  date?: string | Date;
  onComplete?: () => void;
  days?: boolean;
  hours?: boolean;
  minutes?: boolean;
  seconds?: boolean;
  className?: CountdownClassNameComponent;
};

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

type TimerProps = {
  label: string;
  value: number;
  show?: boolean;
  className?: CountdownClassNameComponent;
};

const Timer = memo(({ label, value, show, className }: TimerProps) => {
  return (
    <ConditionalRenderer condition={show}>
      <div
        className={twMerge(
          'flex flex-col p-2.5 bg-accent/80 rounded-xl text-neutral-content justify-between',
          className?.item,
        )}
      >
        <span
          className={twMerge(
            'countdown font-poppins text-[1.5rem] font-600',
            className?.value,
          )}
        >
          <span
            style={{ '--value': value } as CSSProperties}
            aria-live="polite"
            aria-label={`${value} ${label}`}
          >
            {value}
          </span>
        </span>
        <span className={twMerge('text-sm', className?.label)}>{label}</span>
      </div>
    </ConditionalRenderer>
  );
});

const Countdown = memo(
  ({
    date,
    onComplete,
    days,
    hours,
    minutes,
    seconds,
    className,
  }: CountdownProps) => {
    const [timeLeft, setTimeLeft] = useState<TimeLeft>({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    });

    const { t } = useTranslation('translation', {
      keyPrefix: 'nextLessonDisplay.countdown',
    });

    const getTimeCalculations = (difference: number) => ({
      days: Math.floor(difference / MILLISECONDS_PER_DAY),
      hours: Math.floor(
        (difference % MILLISECONDS_PER_DAY) / MILLISECONDS_PER_HOUR,
      ),
      minutes: Math.floor(
        (difference % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE,
      ),
      seconds: Math.floor(
        (difference % MILLISECONDS_PER_MINUTE) / MILLISECONDS_PER_SECOND,
      ),
    });

    useEffect(() => {
      if (!date) return;

      const targetDate = date instanceof Date ? date : new Date(date);

      const calculateTimeLeft = () => {
        const difference = targetDate.getTime() - new Date().getTime();

        if (difference <= 0) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          onComplete?.();
          return;
        }

        const timeCalculations = getTimeCalculations(difference);
        setTimeLeft(timeCalculations);
      };

      calculateTimeLeft();
      const timer = setInterval(calculateTimeLeft, 1000);

      return () => clearInterval(timer);
    }, [date, onComplete]);

    return (
      <ConditionalRenderer condition={date}>
        <ul
          className={twMerge(
            'grid grid-flow-col gap-2.5 text-center auto-cols-max w-fit',
            className?.base,
          )}
        >
          <Timer
            label={t('days')}
            value={timeLeft.days}
            show={days}
            className={className}
          />
          <Timer
            label={t('hrs')}
            value={timeLeft.hours}
            show={hours}
            className={className}
          />
          <Timer
            label={t('min')}
            value={timeLeft.minutes}
            show={minutes}
            className={className}
          />
          <Timer
            label={t('sec')}
            value={timeLeft.seconds}
            show={seconds}
            className={className}
          />
        </ul>
      </ConditionalRenderer>
    );
  },
);

export default Countdown;
